/**
 * PobockaEditModal — edit one pobočka (per-section modal, as the other modules' detail pages).
 * Create is the full-page `PobockaForm`; both share `PobockaFormBody`. PUTs a slice-only payload.
 */

import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Modal, DialogCrown } from '@/components/Modal';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useUpdatePobockaMutation, type PujcovnaPobocka } from './api';
import { slugSeMeni } from './otviraci-doba-helpers';
import { POBOCKA_FORM_ID, PobockaFormBody, pobockaSeed, pobockaToBody } from './pobocka-section-forms';

interface PobockaEditModalProps {
  pobocka: PujcovnaPobocka;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PobockaEditModal({ pobocka, open, onOpenChange }: PobockaEditModalProps) {
  const update = useUpdatePobockaMutation(pobocka.id);
  const { confirm, dialog } = useConfirm();

  return (
    <>
      <Modal open={open} onOpenChange={onOpenChange} maxWidth={640}>
        <DialogCrown
          eyebrow="Pobočka"
          title={
            <>
              Upravit <em>{pobocka.nazev}</em>
            </>
          }
          sub="Název, adresa, kontakty, odkaz na webu a otevírací doba pobočky."
        />
        <Modal.Body className="px-8 py-5">
          <PobockaFormBody
            initial={pobockaSeed(pobocka)}
            busy={update.isPending}
            onSubmit={async (form) => {
              if (!form.nazev.trim()) {
                toast.error('Zadejte název pobočky.');
                return;
              }
              // The key is part of the public URL: changing or clearing it breaks every link already out there.
              if (slugSeMeni(pobocka.slug, form.slug)) {
                const ok = await confirm({
                  title: (
                    <>
                      Změnit <em>odkaz na webu</em>?
                    </>
                  ),
                  description: `Dosavadní odkazy na pobočku (…?pobocka=${pobocka.slug}) přestanou fungovat${
                    form.slug.trim() ? '.' : ' a pobočka zmizí z veřejné nabídky.'
                  }`,
                  tone: 'danger',
                  confirmLabel: 'Změnit odkaz',
                });
                if (!ok) return;
              }
              update.mutate(pobockaToBody(form), {
                onSuccess: () => {
                  toast.ok('Pobočka uložena.');
                  onOpenChange(false);
                },
                onError: (err) =>
                  toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit pobočku.'}`),
              });
            }}
          />
        </Modal.Body>
        <Modal.Foot
          actions={
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={update.isPending}>
                Zrušit
              </Button>
              <Button variant="ink" type="submit" form={POBOCKA_FORM_ID} disabled={update.isPending}>
                Uložit
              </Button>
            </>
          }
        />
      </Modal>
    {dialog}
    </>
  );
}
